import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { toast } from 'sonner';
import { ArrowLeft, AlertTriangle, ChevronLeft, ChevronRight, Maximize2, ZoomIn, ZoomOut, RotateCcw, RotateCw, X } from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PresentacionViewer = ({ user }) => {
  const navigate = useNavigate();
  const { presentationId } = useParams();
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchEndRef = useRef(null);
  const lastTouchDistance = useRef(null);
  
  // State
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isBlurred, setIsBlurred] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isPinching, setIsPinching] = useState(false);
  
  // Screen state
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  
  // LANDSCAPE STATE - Auto-detected from device orientation
  const [isLandscape, setIsLandscape] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Zoom state
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  // MANUAL rotation: user taps a button to force landscape (CSS-rotated slide),
  // independent of the physical device orientation.
  const [manualLandscape, setManualLandscape] = useState(false);

  // Get token for PDF request
  const token = localStorage.getItem('token');

  // Detect landscape orientation and enter fullscreen
  useEffect(() => {
    const applyOrientation = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // Prefer the CSS orientation media query (most reliable on mobile),
      // fall back to comparing dimensions.
      const mqLandscape = window.matchMedia
        ? window.matchMedia('(orientation: landscape)').matches
        : w > h;
      const isLandscapeMode = mqLandscape || w > h;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = w <= 1024;

      // Apply landscape mode on small screens or touch devices when in landscape orientation
      if ((isTouchDevice || isSmallScreen) && isLandscapeMode) {
        setIsLandscape(true);
        // Only try fullscreen on touch devices (mobile)
        if (isTouchDevice) {
          enterFullscreen();
        }
      } else if ((isTouchDevice || isSmallScreen) && !isLandscapeMode) {
        setIsLandscape(false);
        if (isTouchDevice) {
          exitFullscreen();
        }
      } else {
        // Desktop in any orientation - no landscape mode
        setIsLandscape(false);
      }

      // Update screen dimensions
      setScreenWidth(w);
      setScreenHeight(h);
      setIsMobile(isSmallScreen || isTouchDevice);
    };

    const checkOrientation = () => {
      applyOrientation();
      // Mobile browsers often report stale innerWidth/innerHeight right after
      // an orientationchange — re-read once the viewport has settled.
      setTimeout(applyOrientation, 250);
      setTimeout(applyOrientation, 600);
    };

    // Enter fullscreen mode
    const enterFullscreen = async () => {
      try {
        const elem = containerRef.current || document.documentElement;
        if (elem.requestFullscreen) {
          await elem.requestFullscreen();
          setIsFullscreen(true);
        } else if (elem.webkitRequestFullscreen) {
          await elem.webkitRequestFullscreen();
          setIsFullscreen(true);
        } else if (elem.msRequestFullscreen) {
          await elem.msRequestFullscreen();
          setIsFullscreen(true);
        }
      } catch (err) {
        // Fullscreen might fail if not triggered by user gesture
        console.log('Fullscreen request failed:', err);
      }
    };

    // Exit fullscreen mode
    const exitFullscreen = async () => {
      try {
        if (document.fullscreenElement || document.webkitFullscreenElement) {
          if (document.exitFullscreen) {
            await document.exitFullscreen();
          } else if (document.webkitExitFullscreen) {
            await document.webkitExitFullscreen();
          }
          setIsFullscreen(false);
        }
      } catch (err) {
        console.log('Exit fullscreen failed:', err);
      }
    };

    // Listen for fullscreen changes
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(isCurrentlyFullscreen);
    };

    // Initial check
    checkOrientation();
    
    // Listen for orientation/resize changes
    const mql = window.matchMedia ? window.matchMedia('(orientation: landscape)') : null;
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    if (mql) {
      if (mql.addEventListener) mql.addEventListener('change', checkOrientation);
      else if (mql.addListener) mql.addListener(checkOrientation);
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      if (mql) {
        if (mql.removeEventListener) mql.removeEventListener('change', checkOrientation);
        else if (mql.removeListener) mql.removeListener(checkOrientation);
      }
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Set PDF URL
  useEffect(() => {
    if (presentationId && token) {
      setPdfUrl(`${API}/presentations/view/${presentationId}?token=${token}`);
    }
  }, [presentationId, token]);

  // Auto-hide controls
  useEffect(() => {
    let timeout;
    if (showControls && !isPinching) {
      timeout = setTimeout(() => setShowControls(false), 4000);
    }
    return () => clearTimeout(timeout);
  }, [showControls, currentPage, isPinching]);

  // Reset zoom when changing pages
  useEffect(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [currentPage]);

  // PDF loaded callback
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setCurrentPage(1);
  };

  // Page navigation
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= numPages) {
      setPageLoading(true);
      setCurrentPage(page);
      setShowControls(true);
    }
  }, [numPages]);

  const nextPage = useCallback(() => {
    if (scale === 1) goToPage(currentPage + 1);
  }, [currentPage, goToPage, scale]);

  const prevPage = useCallback(() => {
    if (scale === 1) goToPage(currentPage - 1);
  }, [currentPage, goToPage, scale]);

  // Zoom functions
  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 4));
    setShowControls(true);
  };

  const zoomOut = () => {
    setScale(prev => {
      const newScale = Math.max(prev - 0.5, 1);
      if (newScale === 1) setPosition({ x: 0, y: 0 });
      return newScale;
    });
    setShowControls(true);
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setShowControls(true);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextPage();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevPage();
      } else if (e.key === 'Escape') {
        navigate(-1);
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        zoomOut();
      }
      
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
        e.preventDefault();
        toast.error('Acción no permitida');
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [nextPage, prevPage, navigate]);

  // Touch handling
  const getDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      setIsPinching(true);
      lastTouchDistance.current = getDistance(e.touches);
    } else if (e.touches.length === 1) {
      touchEndRef.current = null;
      touchStartRef.current = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      };
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastTouchDistance.current) {
      e.preventDefault();
      const currentDistance = getDistance(e.touches);
      const delta = currentDistance - lastTouchDistance.current;
      
      if (Math.abs(delta) > 5) {
        setScale(prev => Math.min(Math.max(prev + delta * 0.008, 1), 4));
        lastTouchDistance.current = currentDistance;
      }
    } else if (e.touches.length === 1 && scale > 1 && touchStartRef.current) {
      const deltaX = e.touches[0].clientX - touchStartRef.current.x;
      const deltaY = e.touches[0].clientY - touchStartRef.current.y;
      setPosition(prev => ({
        x: prev.x + deltaX * 0.5,
        y: prev.y + deltaY * 0.5
      }));
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      };
    } else if (e.touches.length === 1) {
      touchEndRef.current = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      };
    }
  };

  const onTouchEnd = () => {
    if (isPinching) {
      setIsPinching(false);
      lastTouchDistance.current = null;
      if (scale <= 1) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
      return;
    }
    
    if (!touchStartRef.current || !touchEndRef.current || scale > 1) return;
    
    const distanceX = touchStartRef.current.x - touchEndRef.current.x;
    const distanceY = touchStartRef.current.y - touchEndRef.current.y;
    
    // Horizontal swipe for page navigation (works in both portrait and landscape)
    if (Math.abs(distanceX) > Math.abs(distanceY) && Math.abs(distanceX) > minSwipeDistance) {
      if (distanceX > minSwipeDistance) nextPage();
      else if (distanceX < -minSwipeDistance) prevPage();
    }
    
    setShowControls(true);
  };

  const handleContainerClick = (e) => {
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    setShowControls(true);
  };

  // Protection
  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener('contextmenu', handleContextMenu);
    return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsBlurred(document.hidden);
      if (!document.hidden) setTimeout(() => setIsBlurred(false), 500);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', () => setIsBlurred(true));
    window.addEventListener('focus', () => setTimeout(() => setIsBlurred(false), 300));
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  const handleBack = () => navigate(-1);

  // Effective landscape = physical device landscape OR user-forced (button).
  const deviceLandscape = screenWidth > screenHeight;
  const rotateNeeded = manualLandscape && !deviceLandscape; // CSS-rotate the slide 90°
  const effectiveLandscape = isLandscape || manualLandscape;

  // Calculate page dimensions - FIT TO SCREEN in landscape
  const getPageDimensions = () => {
    if (rotateNeeded) {
      // Portrait device, user tapped the rotate button → the slide is CSS-rotated 90°.
      // After rotation the slide's height becomes the on-screen width and vice-versa,
      // so fit it into a box of width=screenHeight, height=screenWidth.
      const availableHeight = screenWidth - 16;   // on-screen horizontal extent after rotation
      const availableWidth = screenHeight - 16;    // on-screen vertical extent after rotation
      const slideAspectRatio = 4 / 3;
      const widthFromHeight = availableHeight * slideAspectRatio;
      if (widthFromHeight <= availableWidth) {
        return { height: availableHeight, width: undefined };
      }
      return { width: availableWidth, height: undefined };
    }
    if (effectiveLandscape) {
      // In landscape mode on mobile: fit slide completely to screen
      // Calculate dimensions to fit a typical slide (4:3 aspect ratio) within viewport
      const availableHeight = screenHeight - 50;  // Leave space for controls
      const availableWidth = screenWidth - 80;    // Leave space for nav arrows
      
      // For a 4:3 slide, width = height * 1.33
      // For a 16:9 slide, width = height * 1.77
      // Use 4:3 as default since it's more common for presentations
      const slideAspectRatio = 4 / 3;  // Most presentation slides
      
      // Calculate which dimension is the constraint
      const widthFromHeight = availableHeight * slideAspectRatio;
      const heightFromWidth = availableWidth / slideAspectRatio;
      
      if (widthFromHeight <= availableWidth) {
        // Height is the constraint
        return { height: availableHeight, width: undefined };
      } else {
        // Width is the constraint
        return { width: availableWidth, height: undefined };
      }
    } else if (!isMobile) {
      // Desktop: limited size
      const headerHeight = 56;
      const footerHeight = 48;
      const availableHeight = screenHeight - headerHeight - footerHeight;
      const maxWidth = Math.min(screenWidth * 0.85, 1000);
      const maxHeight = Math.min(availableHeight * 0.9, maxWidth * 0.75);
      return { width: maxWidth, height: maxHeight };
    } else {
      // Mobile portrait: normal
      const headerHeight = 56;
      const footerHeight = 48;
      return {
        width: screenWidth,
        height: screenHeight - headerHeight - footerHeight
      };
    }
  };

  const pageDims = getPageDimensions();

  if (!pdfUrl) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className={`fixed inset-0 bg-black flex flex-col select-none overflow-hidden ${effectiveLandscape ? 'landscape-mode' : ''}`}
      style={{ 
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: scale > 1 ? 'none' : 'pan-x',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClick={handleContainerClick}
      onCopy={(e) => e.preventDefault()}
      data-testid="presentation-viewer"
    >
      {/* Warning Overlay */}
      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-red-600/90">
          <div className="text-center text-white p-8">
            <AlertTriangle size={64} className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold">⚠️ ALERTA</h2>
            <p>Capturas no permitidas</p>
          </div>
        </div>
      )}

      {/* Header - HIDDEN in landscape mode */}
      {!effectiveLandscape && (
        <header className={`bg-black py-3 px-4 z-40 flex-shrink-0 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          <div className="flex items-center justify-between max-w-5xl mx-auto">
            <button onClick={handleBack} className="flex items-center space-x-2 text-white text-sm" data-testid="back-button">
              <ArrowLeft size={20} />
              <span className="hidden sm:inline">Volver</span>
            </button>
            
            {/* Desktop zoom controls */}
            <div className="hidden sm:flex items-center space-x-2">
              <button onClick={zoomOut} disabled={scale <= 1} className="text-white p-1.5 rounded hover:bg-white/20 disabled:opacity-30">
                <ZoomOut size={18} />
              </button>
              <span className="text-white text-xs w-12 text-center">{Math.round(scale * 100)}%</span>
              <button onClick={zoomIn} disabled={scale >= 4} className="text-white p-1.5 rounded hover:bg-white/20 disabled:opacity-30">
                <ZoomIn size={18} />
              </button>
            </div>
            
            {/* Page indicator in header for mobile portrait */}
            <div className="sm:hidden text-white text-sm">
              {currentPage} / {numPages || '...'}
            </div>
          </div>
        </header>
      )}

      {/* PDF Container */}
      <div 
        className={`flex-1 flex items-center justify-center overflow-hidden transition-all duration-300 ${isBlurred ? 'blur-xl' : ''}`}
        style={{
          transform: scale > 1 ? `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)` : undefined,
          transformOrigin: 'center center',
        }}
      >
        {loading && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
            <div className="text-center text-white">
              <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p>Cargando...</p>
            </div>
          </div>
        )}

        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black z-20">
            <div className="text-center text-white px-6 max-w-sm">
              <AlertTriangle size={48} className="mx-auto mb-4 text-yellow-400" />
              <p className="text-lg font-semibold mb-2">Archivo no disponible</p>
              <p className="text-sm text-gray-300 mb-6">{loadError}</p>
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-2 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors"
              >
                Volver
              </button>
            </div>
          </div>
        )}

        {/* PDF Wrapper - constrains size in landscape */}
        <div 
          className={`flex items-center justify-center ${effectiveLandscape ? 'pdf-wrapper' : ''}`}
          style={rotateNeeded ? { transform: 'rotate(90deg)', transformOrigin: 'center center' } : undefined}
        >
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) => {
              console.error('PDF error:', error);
              setLoading(false);
              setLoadError('No se pudo cargar la presentación. Es posible que el archivo no esté disponible y necesite ser subido nuevamente.');
            }}
            loading={null}
            className="flex items-center justify-center"
          >
            <Page
              pageNumber={currentPage}
              {...(pageDims.width ? { width: pageDims.width } : {})}
              {...(pageDims.height ? { height: pageDims.height } : {})}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              onRenderSuccess={() => setPageLoading(false)}
              loading={
                <div className="flex items-center justify-center" style={{ 
                  width: pageDims.width || 'auto', 
                  height: pageDims.height || 'auto' 
                }}>
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                </div>
              }
              className="shadow-2xl"
            />
          </Document>
        </div>

        {isBlurred && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-30">
            <AlertTriangle size={48} className="text-white opacity-75" />
          </div>
        )}
      </div>

      {/* ============ LANDSCAPE MODE CONTROLS ============ */}
      {effectiveLandscape && (
        <>
          {/* Exit fullscreen button - Top left */}
          <button
            onClick={handleBack}
            className={`fixed top-3 left-3 z-50 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full shadow-lg transition-opacity duration-300 ${
              showControls ? 'opacity-100' : 'opacity-30'
            }`}
            data-testid="exit-landscape-button"
          >
            <X size={20} />
          </button>

          {/* Zoom controls - Top right in landscape */}
          <div className={`fixed top-3 right-3 z-50 flex space-x-2 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-30'}`}>
            <button onClick={zoomOut} disabled={scale <= 1} className="bg-black/60 text-white p-2 rounded-full disabled:opacity-30">
              <ZoomOut size={18} />
            </button>
            <button onClick={zoomIn} disabled={scale >= 4} className="bg-black/60 text-white p-2 rounded-full disabled:opacity-30">
              <ZoomIn size={18} />
            </button>
            {scale > 1 && (
              <button onClick={resetZoom} className="bg-black/60 text-white p-2 rounded-full">
                <RotateCcw size={18} />
              </button>
            )}
          </div>

          {/* Rotate back to portrait (only when rotation was forced by button) */}
          {manualLandscape && (
            <button
              onClick={() => setManualLandscape(false)}
              className={`fixed bottom-3 right-3 z-50 bg-black/70 hover:bg-black text-white p-2.5 rounded-full shadow-lg transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-40'}`}
              data-testid="rotate-back-button"
              aria-label="Volver a vertical"
            >
              <RotateCw size={20} className="rotate-90" />
            </button>
          )}

          {/* Page indicator - Bottom center in landscape */}
          <div className={`fixed bottom-3 left-1/2 -translate-x-1/2 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-40'}`}>
            <div className="bg-black/70 text-white px-4 py-1.5 rounded-full text-sm font-medium">
              {currentPage} / {numPages || '...'}
            </div>
          </div>
        </>
      )}

      {/* Navigation Arrows - Both modes */}
      {numPages > 1 && scale === 1 && (
        <>
          <button
            onClick={prevPage}
            disabled={currentPage <= 1}
            className={`fixed left-2 top-1/2 -translate-y-1/2 z-40 p-2 rounded-full bg-black/50 transition-opacity ${
              currentPage <= 1 ? 'opacity-20' : showControls ? 'opacity-80' : 'opacity-30'
            }`}
            data-testid="prev-page-button"
          >
            <ChevronLeft size={28} className="text-white" />
          </button>
          <button
            onClick={nextPage}
            disabled={currentPage >= numPages}
            className={`fixed right-2 top-1/2 -translate-y-1/2 z-40 p-2 rounded-full bg-black/50 transition-opacity ${
              currentPage >= numPages ? 'opacity-20' : showControls ? 'opacity-80' : 'opacity-30'
            }`}
            data-testid="next-page-button"
          >
            <ChevronRight size={28} className="text-white" />
          </button>
        </>
      )}

      {/* Page Indicator - Portrait mode only (bottom center) */}
      {!isLandscape && !isMobile && (
        <div className={`fixed bottom-16 left-1/2 -translate-x-1/2 z-50 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-60'}`}>
          <div className="bg-black/80 text-white px-4 py-2 rounded-full text-sm font-medium">
            {currentPage} / {numPages || '...'}
          </div>
        </div>
      )}

      {/* Footer - HIDDEN in landscape mode */}
      {!effectiveLandscape && (
        <div className={`bg-black py-2 px-4 text-center text-gray-500 text-xs flex-shrink-0 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
          © Proyecto Residente • Desliza ← → para navegar
        </div>
      )}

      {/* Manual rotate button - Portrait (mobile) → forces landscape via CSS rotation */}
      {!effectiveLandscape && isMobile && (
        <button
          onClick={() => setManualLandscape(true)}
          className={`fixed bottom-20 right-4 z-50 bg-black/70 hover:bg-black text-white p-3 rounded-full shadow-lg transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-50'}`}
          data-testid="rotate-toggle-button"
          aria-label="Rotar presentación a horizontal"
        >
          <RotateCw size={22} />
        </button>
      )}

      {/* Hint for landscape mode - Portrait mobile only */}
      {isMobile && !effectiveLandscape && showControls && currentPage === 1 && (
        <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
          <div className="bg-white/20 text-white px-3 py-1.5 rounded-full text-xs backdrop-blur-sm animate-pulse">
            Toca el botón de rotar para pantalla completa
          </div>
        </div>
      )}

      <style>{`
        .react-pdf__Document {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        .react-pdf__Page {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .react-pdf__Page__canvas {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }
        /* Force contain in landscape mode - use viewport units */
        .landscape-mode .react-pdf__Page,
        .landscape-mode .react-pdf__Page__canvas {
          max-height: 85vh !important;
          width: auto !important;
        }
        .landscape-mode .pdf-wrapper {
          max-height: 85vh;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default PresentacionViewer;
